import axios from 'axios';

const BASE_URL = process.env.REACT_APP_BACKEND_URL;

const api = axios.create({ baseURL: BASE_URL });

// Attach admin OR customer token
api.interceptors.request.use((config) => {
  // prefer admin token for admin routes
  const adminToken = localStorage.getItem('su_token');
  const customerToken = localStorage.getItem('su_customer_token');
  const token = adminToken || customerToken;
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// Handle 401
api.interceptors.response.use(
  (r) => r,
  (error) => {
    if (error.response?.status === 401) {
      if (window.location.pathname.startsWith('/admin') && window.location.pathname !== '/admin/login') {
        localStorage.removeItem('su_token');
        localStorage.removeItem('su_user');
        window.location.href = '/admin/login';
      }
    }
    return Promise.reject(error);
  }
);

// Separate axios instance for customer calls (always uses customer token)
const customerAxios = axios.create({ baseURL: BASE_URL });
customerAxios.interceptors.request.use((config) => {
  const token = localStorage.getItem('su_customer_token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});
customerAxios.interceptors.response.use(
  (r) => r,
  (error) => {
    if (error.response?.status === 401) {
      if (window.location.pathname.startsWith('/account') && window.location.pathname !== '/account/login') {
        localStorage.removeItem('su_customer_token');
        localStorage.removeItem('su_customer');
        window.location.href = '/account/login';
      }
    }
    return Promise.reject(error);
  }
);

export default api;

// ── Public / Guest API ─────────────────────────────────────────────────────────
export const publicApi = {
  calculatePricing: (visitDate, guests) =>
    api.get(`/api/public/pricing/calculate?visit_date=${visitDate}&guests=${guests}`),
  createCheckIn: (data) => api.post('/api/public/checkins', data),
  createPaymentOrder: (data) => api.post('/api/public/payments/create-order', data),
  getPaymentStatus: (orderId) => api.get(`/api/public/payments/${orderId}`),
  getPaymentSession: (orderId) => api.get(`/api/public/payments/${orderId}/session`),
  getCheckInByRef: (referenceId) => api.get(`/api/public/checkins/${referenceId}`),
  getSourceQR: (code) => api.get(`/api/public/source-qrs/${code}`),
  lookupCustomer: (phone) => api.get(`/api/public/customers/lookup?phone=${encodeURIComponent(phone)}`),
  
  // OTP Auth (Smart: WhatsApp-first with SMS fallback)
  sendOTPSmart: (phone) => api.post('/api/auth/send-otp-smart', { phone }),
  sendOTPWhatsApp: (phone) => api.post('/api/auth/send-otp-whatsapp', { phone }),
  sendOTPSMSFallback: (phone) => api.post('/api/auth/send-otp-sms-fallback', { phone }),
  verifyOTP: (phone, otp) => api.post('/api/auth/verify-otp', { phone, otp }),

  // Phase 11 — Branded short-link resolver (login-less)
  resolveShortLink: (code) => api.get(`/api/v2/shortlinks/${encodeURIComponent(code)}`),

  // Pay-at-Gate — customer redeems a 6-digit code issued by staff
  redeemGateCode: (checkinId, code) =>
    api.post('/api/public/payments/gate-code/redeem', {
      checkin_id: checkinId,
      code,
    }),

  // ── Special Events (Nazakat & Shararat) ────────────────────────────────
  sxeGet:            (slug)                 => api.get(`/api/public/special-events/${slug}`),
  sxeValidateInvite: (slug, code)           => api.post(`/api/public/special-events/${slug}/invite/validate`, { code }),
  sxeValidateCoupon: (slug, code)           => api.post(`/api/public/special-events/${slug}/coupon/validate`, { code }),
  sxeCreateBooking:  (slug, payload)        => api.post(`/api/public/special-events/${slug}/booking`, payload),
  sxeGetBookingByToken: (qrToken)           => api.get(`/api/public/special-events/booking/${qrToken}`),
};

// ── Customer Auth & Dashboard API ─────────────────────────────────────────────
export const customerApi = {
  signup: (data) => customerAxios.post('/api/customer/auth/signup', data),
  login: (data) => customerAxios.post('/api/customer/auth/login', data),
  loginOTP: (phone, otp) => customerAxios.post('/api/customer/auth/login-otp', { phone, otp }),
  me: () => customerAxios.get('/api/customer/auth/me'),
  forgotPassword: (login) => customerAxios.post('/api/customer/auth/forgot-password', { login }),
  setPassword: (data) => customerAxios.post('/api/customer/profile/set-password', data),
  updateProfile: (data) => customerAxios.patch('/api/customer/profile', data),
  // Wallet (Scratch Points)
  getWalletBalance:      ()       => customerAxios.get('/api/customer/wallet/balance'),
  getWalletTransactions: (params) => customerAxios.get('/api/customer/wallet/transactions', { params }),
  generateWalletQR:      ()       => customerAxios.post('/api/customer/wallet/qr-token'),
  getBookings: (params) => customerAxios.get('/api/customer/bookings', { params }),
  getEvents: (params) => customerAxios.get('/api/customer/events', { params }),
  getEvent: (slug) => customerAxios.get(`/api/customer/events/${slug}`),
};

// ── Admin API ──────────────────────────────────────────────────────────────────
export const adminApi = {
  login: (email, password) => api.post('/api/auth/login', { email, password }),
  getMe: () => api.get('/api/auth/me'),

  // Wallet — admin redemption (Phase 2)
  decodeWalletQR: (token) => api.post('/api/admin/wallet/qr/decode', { token }),
  redeemWalletQR: (token, amount) => api.post('/api/admin/wallet/redeem-qr', { token, amount }),

  // Wallet — admin manual OTP redemption (Phase 3)
  initiateOTPRedeem: (phone, amount) =>
    api.post('/api/admin/wallet/initiate-otp-redeem', { phone, amount }),
  confirmOTPRedeem: (session_id, otp_code) =>
    api.post('/api/admin/wallet/confirm-otp-redeem', { session_id, otp_code }),

  // Wallet — analytics (Phase 4) — read-only KPIs / top users / expiring soon
  getPointsAnalyticsSummary:      ()       => api.get('/api/admin/points/analytics/summary'),
  getPointsAnalyticsTopUsers:     (limit = 10) =>
    api.get('/api/admin/points/analytics/top-users', { params: { limit } }),
  getPointsAnalyticsExpiringSoon: (window_minutes = 120, limit = 20) =>
    api.get('/api/admin/points/analytics/expiring-soon', {
      params: { window_minutes, limit },
    }),

  // Wallet — Phase 5 Loyalty Engine
  lookupCustomerForIssue: (phone) =>
    api.get('/api/admin/wallet/lookup-customer', { params: { phone } }),
  issueLoyalty: (phone, amount, reason) =>
    api.post('/api/admin/wallet/issue-loyalty', { phone, amount, reason }),
  issuePromo: (phone, amount, expiry_hours, campaign_name) =>
    api.post('/api/admin/wallet/issue-promo', { phone, amount, expiry_hours, campaign_name }),
  reverseCredit: (phone, amount, reason, reference_txn_id) =>
    api.post('/api/admin/wallet/reverse', { phone, amount, reason, reference_txn_id }),
  listLoyaltyRules: () => api.get('/api/admin/wallet/loyalty-rules'),

  getDashboard: () => api.get('/api/admin/dashboard'),

  // Special Events admin
  sxeAdminGet:       (slug)                 => api.get(`/api/admin/special-events/${slug}`),
  sxeAdminPatch:     (slug, patch)          => api.patch(`/api/admin/special-events/${slug}`, patch),
  sxeAdminAddCode:   (slug, code, maxUses)  => api.post(`/api/admin/special-events/${slug}/invite-codes`, { code, max_uses: maxUses || 0 }),
  sxeAdminDelCode:   (slug, code)           => api.delete(`/api/admin/special-events/${slug}/invite-codes/${encodeURIComponent(code)}`),
  sxeAdminPatchCoupon:(slug, code, patch)   => api.patch(`/api/admin/special-events/${slug}/coupons/${encodeURIComponent(code)}`, patch),
  // Special Events — Bookings & Scan
  sxeAdminListBookings: (slug, params)        => api.get(`/api/admin/special-events/${slug}/bookings`, { params }),
  sxeAdminGetBooking:   (slug, id)            => api.get(`/api/admin/special-events/${slug}/bookings/${id}`),
  sxeAdminPatchBooking: (slug, id, payload)   => api.patch(`/api/admin/special-events/${slug}/bookings/${id}`, payload),
  sxeAdminScan:         (slug, payload)       => api.post(`/api/admin/special-events/${slug}/scan`, payload),

  // Gate Codes (Pay at Gate)
  listGateCodes:   (params) => api.get('/api/admin/gate-codes', { params }),
  createGateCode:  (data)   => api.post('/api/admin/gate-codes', data),
  cancelGateCode:  (codeId) => api.post(`/api/admin/gate-codes/${codeId}/cancel`),

  getCheckIns: (params) => api.get('/api/admin/checkins', { params }),
  getCheckIn: (id) => api.get(`/api/admin/checkins/${id}`),
  updateCheckIn: (id, data) => api.patch(`/api/admin/checkins/${id}`, data),

  getPayments: (params) => api.get('/api/admin/payments', { params }),
  getPayment: (id) => api.get(`/api/admin/payments/${id}`),
  createManualPayment: (data) => api.post('/api/admin/payments/manual', data),

  getPricingRules: () => api.get('/api/admin/pricing-rules'),
  createPricingRule: (data) => api.post('/api/admin/pricing-rules', data),
  updatePricingRule: (id, data) => api.patch(`/api/admin/pricing-rules/${id}`, data),
  deletePricingRule: (id) => api.delete(`/api/admin/pricing-rules/${id}`),

  getUsers: () => api.get('/api/admin/users'),
  createUser: (data) => api.post('/api/admin/users', data),
  updateUser: (id, data) => api.patch(`/api/admin/users/${id}`, data),

  getReportTotals: (dateFrom, dateTo) =>
    api.get(`/api/admin/reports/totals?date_from=${dateFrom}&date_to=${dateTo}`),

  exportCheckIns: (dateFrom, dateTo) => {
    const p = new URLSearchParams();
    if (dateFrom) p.append('date_from', dateFrom);
    if (dateTo) p.append('date_to', dateTo);
    const token = localStorage.getItem('su_token');
    window.open(`${BASE_URL}/api/admin/export/checkins?${p.toString()}&token=${token}`, '_blank');
  },
  exportPayments: (dateFrom, dateTo) => {
    const p = new URLSearchParams();
    if (dateFrom) p.append('date_from', dateFrom);
    if (dateTo) p.append('date_to', dateTo);
    const token = localStorage.getItem('su_token');
    window.open(`${BASE_URL}/api/admin/export/payments?${p.toString()}&token=${token}`, '_blank');
  },

  getSourceQRs: () => api.get('/api/admin/source-qrs'),
  createSourceQR: (data) => api.post('/api/admin/source-qrs', data),
  deleteSourceQR: (id) => api.delete(`/api/admin/source-qrs/${id}`),

  // Customers
  getCustomers: (params) => api.get('/api/admin/customers', { params }),
  getCustomer: (id) => api.get(`/api/admin/customers/${id}`),
  createCustomer: (data) => api.post('/api/admin/customers', data),
  updateCustomer: (id, data) => api.patch(`/api/admin/customers/${id}`, data),
  // Customer moderation actions
  suspendCustomer:  (id, reason) => api.post(`/api/admin/customers/${id}/suspend`,  { reason }),
  activateCustomer: (id, reason) => api.post(`/api/admin/customers/${id}/activate`, { reason }),
  banCustomer:      (id, reason) => api.post(`/api/admin/customers/${id}/ban`,      { reason }),
  unbanCustomer:    (id, reason) => api.post(`/api/admin/customers/${id}/unban`,    { reason }),
  deleteCustomer:   (id, { hard = false, reason } = {}) =>
    api.delete(`/api/admin/customers/${id}`, { params: { hard, reason } }),
  restoreCustomer:  (id, reason) => api.post(`/api/admin/customers/${id}/restore`,  { reason }),

  // Events
  getEvents: (params) => api.get('/api/admin/events', { params }),
  createEvent: (data) => api.post('/api/admin/events', data),
  updateEvent: (id, data) => api.patch(`/api/admin/events/${id}`, data),
  deleteEvent: (id) => api.delete(`/api/admin/events/${id}`),
  
  // Generic methods for dynamic routes
  get: (path, config) => api.get(`/api/admin${path}`, config),
  post: (path, data, config) => api.post(`/api/admin${path}`, data, config),
  patch: (path, data, config) => api.patch(`/api/admin${path}`, data, config),
  delete: (path, config) => api.delete(`/api/admin${path}`, config),
};

// ── Marketing API ──────────────────────────────────────────────────────────────
export const marketingApi = {
  // Dashboard
  getDashboard: () => api.get('/api/admin/marketing/dashboard'),

  // Templates
  getTemplates: (params) => api.get('/api/admin/marketing/templates', { params }),
  getTemplate: (id) => api.get(`/api/admin/marketing/templates/${id}`),
  createTemplate: (data) => api.post('/api/admin/marketing/templates', data),
  updateTemplate: (id, data) => api.patch(`/api/admin/marketing/templates/${id}`, data),
  deleteTemplate: (id) => api.delete(`/api/admin/marketing/templates/${id}`),
  previewTemplate: (id, data) => api.post(`/api/admin/marketing/templates/${id}/preview`, data),

  // Campaigns
  getCampaigns: (params) => api.get('/api/admin/marketing/campaigns', { params }),
  getCampaign: (id) => api.get(`/api/admin/marketing/campaigns/${id}`),
  createCampaign: (data) => api.post('/api/admin/marketing/campaigns', data),
  updateCampaign: (id, data) => api.patch(`/api/admin/marketing/campaigns/${id}`, data),
  deleteCampaign: (id) => api.delete(`/api/admin/marketing/campaigns/${id}`),
  loadFromSegment: (id, data) => api.post(`/api/admin/marketing/campaigns/${id}/load-from-segment`, data),
  startCampaign: (id) => api.post(`/api/admin/marketing/campaigns/${id}/start`),
  pauseCampaign: (id) => api.post(`/api/admin/marketing/campaigns/${id}/pause`),
  resumeCampaign: (id) => api.post(`/api/admin/marketing/campaigns/${id}/resume`),
  getCampaignRecipients: (id, params) => api.get(`/api/admin/marketing/campaigns/${id}/recipients`, { params }),

  // Segments (for wizard)
  getSegments: () => api.get('/api/admin/marketing/segments'),
  getSegment: (id) => api.get(`/api/admin/marketing/segments/${id}`),
};


// ═══════════════════════════════════════════════════════════════════════════
// Event Engine V2 API — Pass Management System
// ═══════════════════════════════════════════════════════════════════════════
// `publicV2Api`   — login-less GET routes
// `customerV2Api` — customer-authenticated routes (bookings + checkout)
// `adminV2Api`    — admin-authenticated routes (events, bookings, refunds)
// ═══════════════════════════════════════════════════════════════════════════
export const publicV2Api = {
  // Events
  listEvents: (params = {}) => axios.get(`${BASE_URL}/api/v2/public/events`, { params }),
  getEvent:   (slug)         => axios.get(`${BASE_URL}/api/v2/public/events/${slug}`),
  // Public ticket (no auth)
  getTicket:  (token)        => axios.get(`${BASE_URL}/api/v2/public/tickets/${token}`),
};

export const customerV2Api = {
  // Phase 10 — Guest checkout (no prior auth required)
  guestCheckoutInit: (data)     => customerAxios.post('/api/v2/checkout/verify-otp-and-init', data),
  createBooking:   (data)     => customerAxios.post('/api/v2/bookings', data),
  getBooking:      (id)       => customerAxios.get(`/api/v2/bookings/${id}`),
  listMyBookings:  (params)   => customerAxios.get('/api/v2/bookings', { params }),
  // Mock-gateway checkout
  createCheckout:  (id)       => customerAxios.post(`/api/v2/payments/checkout/${id}`),
  // Mock webhook (no auth — gateway-style)
  // Events flow now uses Paytm/Razorpay only — mock webhook removed.
};

export const adminV2Api = {
  // Image uploads
  uploadEventImage: (formData) => api.post('/api/v2/admin/uploads', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),

  listEvents:    (params) => api.get('/api/v2/admin/events', { params }),
  getEvent:      (id)     => api.get(`/api/v2/admin/events/${id}`),
  createEvent:   (data)   => api.post('/api/v2/admin/events', data),
  updateEvent:   (id, d)  => api.patch(`/api/v2/admin/events/${id}`, d),
  deleteEvent:   (id, { hard = false } = {}) =>
    api.delete(`/api/v2/admin/events/${id}`, { params: hard ? { hard: true } : {} }),

  // Pass types
  createPassType: (eventId, data) => api.post(`/api/v2/admin/events/${eventId}/pass-types`, data),
  updatePassType: (id, data)      => api.patch(`/api/v2/admin/pass-types/${id}`, data),
  deletePassType: (id)            => api.delete(`/api/v2/admin/pass-types/${id}`),

  // Bookings
  listBookings:   (params) => api.get('/api/v2/admin/bookings', { params }),
  getBooking:     (id)     => api.get(`/api/v2/admin/bookings/${id}`),
  vipDecision:    (id, approve)            => api.post(`/api/v2/admin/bookings/${id}/vip-decision`, { approve }),
  refund:         (id, data)               => api.post(`/api/v2/admin/bookings/${id}/refund`, data),
  scanTicket:     (token)  => api.post('/api/v2/admin/bookings/scan', { token }),
  sweep:          ()       => api.post('/api/v2/admin/sweep-reservations'),
  resendTicket:   (id)     => api.post(`/api/v2/admin/bookings/${id}/resend-ticket`),
  sendReminder:   (id, kind = '24h', force = true) =>
    api.post(`/api/v2/admin/bookings/${id}/send-reminder`, null, { params: { kind, force } }),
  sweepReminders: ()       => api.post('/api/v2/admin/reminders/sweep'),

  // Analytics (Phase 8 — Revenue Dashboard)
  analyticsKpis:             ()                 => api.get('/api/v2/admin/analytics/kpis'),
  analyticsRevenueTimeline:  (days = 7)         => api.get('/api/v2/admin/analytics/revenue-timeline', { params: { days } }),
  analyticsEventPerformance: (params = {})      => api.get('/api/v2/admin/analytics/event-performance', { params }),
  analyticsPaymentBreakdown: (days = null)      => api.get('/api/v2/admin/analytics/payment-breakdown', { params: days ? { days } : {} }),
};


// ─── Promoter Engine (P1) ──────────────────────────────────────────────────
// Referral tracking — public; uses a separate axios so the httpOnly cookie
// round-trips correctly (withCredentials).
const refAxios = axios.create({ baseURL: BASE_URL, withCredentials: true });

export const promoterApi = {
  // Referral cookie binding (public; no auth)
  trackRef: (code)  => refAxios.post('/api/v2/ref/track', { code }),
  whoAmIRef: ()     => refAxios.get('/api/v2/ref/me'),

  // Customer-side (uses customer token)
  me:         ()                => customerAxios.get('/api/customer/promoter/me'),
  stats:      ()                => customerAxios.get('/api/customer/promoter/stats'),
  ledger:     (params = {})     => customerAxios.get('/api/customer/promoter/ledger', { params }),
  requestPayout: (amount)       => customerAxios.post('/api/customer/promoter/payout-request', { amount }),

  // Admin (uses admin token via shared `api` instance)
  adminList:           (params = {})     => api.get('/api/admin/promoters',                { params }),
  adminCreate:         (data)            => api.post('/api/admin/promoters', data),
  adminUpdate:         (id, data)        => api.patch(`/api/admin/promoters/${id}`,        data),
  adminLedger:         (id, params = {}) => api.get(`/api/admin/promoters/${id}/ledger`,   { params }),
  adminPayoutsList:    (params = {})     => api.get('/api/admin/promoter-payouts',         { params }),
  adminPayoutApprove:  (id, data = {})   => api.post(`/api/admin/promoter-payouts/${id}/approve`, data),
  adminPayoutReject:   (id, data = {})   => api.post(`/api/admin/promoter-payouts/${id}/reject`,  data),
  adminModelsList:     ()                => api.get('/api/admin/promoter-commission-models'),
  adminModelsCreate:   (data)            => api.post('/api/admin/promoter-commission-models', data),
  adminModelsUpdate:   (id, data)        => api.patch(`/api/admin/promoter-commission-models/${id}`, data),
  adminSweeperRun:     ()                => api.post('/api/admin/promoter-sweeper/run'),

  // ── Phase P2: Onboarding (invites + applications + KYC) ─────────────────
  // Admin
  adminInvitesList:    (params = {})     => api.get('/api/admin/promoter-invites',         { params }),
  adminInviteCreate:   (data)            => api.post('/api/admin/promoter-invites', data),
  adminAppsList:       (params = {})     => api.get('/api/admin/promoter-applications',    { params }),
  adminAppDetail:      (id)              => api.get(`/api/admin/promoter-applications/${id}`),
  adminAppApprove:     (id, data = {})   => api.post(`/api/admin/promoter-applications/${id}/approve`, data),
  adminAppReject:      (id, reason)      => api.post(`/api/admin/promoter-applications/${id}/reject`, { reason }),
  // Public — invite validation + multipart submit
  publicValidateInvite: (code)           => axios.post(`${BASE_URL}/api/public/promoter-invites/validate`, { code }),
  publicSubmitApplication: (formData)    => axios.post(`${BASE_URL}/api/public/promoter/applications/submit`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
};
